<template>
  <footer class="sticky-action-bar" :data-action-state="state">
    <div
      class="sticky-action-bar__status"
      :role="state === 'error' ? 'alert' : 'status'"
      :aria-live="state === 'error' ? 'assertive' : 'polite'"
    >
      <q-icon :name="statusIcon" aria-hidden="true" />
      <span>{{ statusMessage }}</span>
    </div>
    <div class="sticky-action-bar__actions">
      <slot name="secondary">
        <q-btn
          v-if="showCancel"
          flat
          no-caps
          :disable="state === 'saving' || !dirty"
          :label="cancelLabel"
          @click="$emit('cancel')"
        />
      </slot>
      <q-btn
        color="primary"
        no-caps
        :data-testid="primaryTestId || undefined"
        :loading="state === 'saving'"
        :disable="state === 'saving' || !dirty"
        :label="primaryLabel"
        @click="$emit('primary')"
      />
    </div>
  </footer>
</template>

<script>
const ACTION_STATES = Object.freeze(['idle', 'saving', 'saved', 'error'])

export default {
  name: 'StickyActionBar',
  emits: ['cancel', 'primary'],
  props: {
    state: {
      type: String,
      default: 'idle',
      validator: (value) => ACTION_STATES.includes(value)
    },
    dirty: {
      type: Boolean,
      default: false
    },
    primaryLabel: {
      type: String,
      default: 'Сохранить'
    },
    cancelLabel: {
      type: String,
      default: 'Отменить изменения'
    },
    showCancel: {
      type: Boolean,
      default: true
    },
    errorMessage: {
      type: String,
      default: 'Не удалось сохранить изменения'
    },
    primaryTestId: {
      type: String,
      default: ''
    }
  },
  computed: {
    statusMessage() {
      if (this.state === 'saving') return 'Сохраняем изменения…'
      if (this.state === 'saved') return 'Изменения сохранены'
      if (this.state === 'error') return this.errorMessage
      return this.dirty ? 'Есть несохраненные изменения' : 'Изменений нет'
    },
    statusIcon() {
      if (this.state === 'saving') return 'sync'
      if (this.state === 'saved') return 'check_circle'
      if (this.state === 'error') return 'error_outline'
      return this.dirty ? 'edit' : 'info'
    }
  }
}
</script>

<style scoped>
.sticky-action-bar {
  position: sticky;
  z-index: 5;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sid-space-4);
  min-height: 4.5rem;
  margin-top: var(--sid-space-6);
  padding: var(--sid-space-3) var(--sid-space-4) calc(var(--sid-space-3) + env(safe-area-inset-bottom));
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface) var(--sid-radius-surface) 0 0;
  background: var(--sid-color-surface-0);
  box-shadow: var(--sid-elevation-2);
}

.sticky-action-bar__status,
.sticky-action-bar__actions {
  display: flex;
  align-items: center;
  gap: var(--sid-space-2);
}

.sticky-action-bar__status {
  min-width: 0;
  color: var(--sid-color-text-secondary);
}

.sticky-action-bar[data-action-state='error'] .sticky-action-bar__status {
  color: var(--sid-color-danger);
}

.sticky-action-bar[data-action-state='saved'] .sticky-action-bar__status {
  color: var(--sid-color-success);
}

@media (max-width: 599px) {
  .sticky-action-bar {
    position: static;
    align-items: stretch;
    flex-direction: column;
  }

  .sticky-action-bar__actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .sticky-action-bar__actions :deep(.q-btn) {
    width: 100%;
    min-height: var(--sid-touch-target);
  }
}
</style>
